import { customColor } from '../domain/palettes';
import { clampDim, createPattern, newId } from '../domain/pattern';
import { EMPTY, EMPTY_COLOR } from '../domain/types';
import type { BeadColor, BeadSizeId, Pattern, StitchId } from '../domain/types';

export const FILE_FORMAT = 'beading-designer';
export const FILE_VERSION = 1;
export const FILE_EXT = '.bead.json';

const STITCHES: StitchId[] = ['square', 'loom', 'peyote', 'brick', 'herringbone', 'crochet'];
const SIZES: BeadSizeId[] = ['delica11', 'delica10', 'delica8', 'round15', 'round11', 'round8', 'round6', 'square'];

function encodeCells(cells: Uint16Array): string {
  const bytes = new Uint8Array(cells.buffer, cells.byteOffset, cells.byteLength);
  let bin = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(bin);
}

function decodeCells(b64: string, expected: number): Uint16Array {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const cells = new Uint16Array(bytes.buffer, 0, Math.floor(bytes.length / 2));
  if (cells.length === expected) return new Uint16Array(cells);
  const out = new Uint16Array(expected);
  out.set(cells.subarray(0, Math.min(cells.length, expected)));
  return out;
}

export function serializePattern(pattern: Pattern): string {
  return JSON.stringify(
    {
      format: FILE_FORMAT,
      version: FILE_VERSION,
      pattern: {
        id: pattern.id,
        name: pattern.name,
        stitch: pattern.stitch,
        beadSize: pattern.beadSize,
        width: pattern.width,
        height: pattern.height,
        oddCount: pattern.oddCount ?? false,
        ropeShift: pattern.ropeShift ?? 1,
        palette: pattern.palette,
        meta: pattern.meta,
        cells: encodeCells(pattern.cells),
      },
    },
    null,
    0,
  );
}

function sanitizePalette(raw: unknown): BeadColor[] {
  const out: BeadColor[] = [EMPTY_COLOR];
  if (!Array.isArray(raw)) return out;
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const e = entry as Partial<BeadColor>;
    if (e.id === 'empty') continue;
    if (typeof e.hex !== 'string') continue;
    out.push({
      id: typeof e.id === 'string' ? e.id : `c${out.length}`,
      code: typeof e.code === 'string' ? e.code : '',
      name: typeof e.name === 'string' ? e.name : e.hex,
      hex: e.hex,
      brand: typeof e.brand === 'string' ? e.brand : '',
      finish: typeof e.finish === 'string' ? e.finish : undefined,
    });
  }
  return out;
}

function fromNative(raw: any): Pattern {
  const p = raw.pattern ?? raw;
  const width = clampDim(Number(p.width));
  const height = clampDim(Number(p.height));
  const palette = sanitizePalette(p.palette);
  const pattern = createPattern({
    name: typeof p.name === 'string' ? p.name : 'Imported pattern',
    stitch: STITCHES.includes(p.stitch) ? p.stitch : 'peyote',
    beadSize: SIZES.includes(p.beadSize) ? p.beadSize : 'delica11',
    width,
    height,
  });
  pattern.palette = palette;
  pattern.oddCount = Boolean(p.oddCount);
  pattern.ropeShift = Number(p.ropeShift) || 1;
  if (p.meta && typeof p.meta === 'object') {
    pattern.meta = {
      author: String(p.meta.author ?? ''),
      notes: String(p.meta.notes ?? ''),
      created: Number(p.meta.created) || Date.now(),
      modified: Number(p.meta.modified) || Date.now(),
    };
  }
  if (typeof p.cells === 'string') {
    pattern.cells = decodeCells(p.cells, width * height);
  } else if (Array.isArray(p.cells)) {
    const flat = p.cells.flat();
    for (let i = 0; i < Math.min(flat.length, pattern.cells.length); i++) {
      pattern.cells[i] = Number(flat[i]) || 0;
    }
  }
  // Guard against indices pointing past the palette.
  for (let i = 0; i < pattern.cells.length; i++) {
    if (pattern.cells[i] >= pattern.palette.length) pattern.cells[i] = EMPTY;
  }
  pattern.id = newId();
  return pattern;
}

/**
 * Build a pattern from a grid of colour strings — the common shape of exports
 * from other tools and of hand-written CSVs. Blank, "-" and "." mean no bead.
 */
export function patternFromGrid(grid: string[][], name = 'Imported grid'): Pattern {
  const height = grid.length;
  const width = Math.max(...grid.map((r) => r.length), 1);
  const pattern = createPattern({ name, width, height, stitch: 'square' });
  const lookup = new Map<string, number>();
  for (let r = 0; r < height; r++) {
    for (let c = 0; c < width; c++) {
      const raw = (grid[r][c] ?? '').trim();
      if (!raw || raw === '-' || raw === '.' || raw === '0') continue;
      const key = raw.toLowerCase();
      let index = lookup.get(key);
      if (index === undefined) {
        const hex = /^#?[0-9a-f]{6}$/i.test(raw)
          ? raw.startsWith('#')
            ? raw.toLowerCase()
            : `#${raw.toLowerCase()}`
          : null;
        const color: BeadColor = hex
          ? customColor(hex)
          : { ...customColor('#888888', raw), code: raw };
        pattern.palette.push(color);
        index = pattern.palette.length - 1;
        lookup.set(key, index);
      }
      pattern.cells[r * width + c] = index;
    }
  }
  return pattern;
}

function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else if (ch === '"') quoted = false;
      else cur += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ',' || ch === ';' || ch === '\t') {
      out.push(cur);
      cur = '';
    } else cur += ch;
  }
  out.push(cur);
  return out;
}

export function parseCsvGrid(text: string, name = 'Imported CSV'): Pattern {
  const rows = text
    .split(/\r?\n/)
    .filter((l) => l.trim().length > 0)
    .map(splitCsvLine);
  if (rows.length === 0) throw new Error('That CSV has no rows');
  return patternFromGrid(rows, name);
}

export interface ParseResult {
  pattern: Pattern;
  note?: string;
}

/** Work out what kind of file this is and turn it into a pattern. */
export function parsePatternFile(text: string, filename: string): ParseResult {
  const trimmed = text.trim();
  const base = filename.replace(/\.[^.]+$/, '').replace(/\.bead$/, '');

  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    let raw: any;
    try {
      raw = JSON.parse(trimmed);
    } catch (err) {
      throw new Error(`${filename} looks like JSON but could not be parsed: ${(err as Error).message}`);
    }
    if (raw?.format === FILE_FORMAT || raw?.pattern) {
      return { pattern: fromNative(raw) };
    }
    // A bare 2D array of colour strings.
    if (Array.isArray(raw) && Array.isArray(raw[0])) {
      return {
        pattern: patternFromGrid(raw.map((r: unknown[]) => r.map(String)), base),
        note: 'Read as a colour grid — stitch set to square, adjust in Pattern settings.',
      };
    }
    if (Array.isArray(raw?.grid)) {
      return {
        pattern: patternFromGrid(raw.grid.map((r: unknown[]) => r.map(String)), raw.name ?? base),
        note: 'Read as a colour grid — check the stitch and bead size.',
      };
    }
    throw new Error(`${filename} is JSON but not a pattern this app recognises`);
  }

  if (/[,;\t]/.test(trimmed)) {
    return {
      pattern: parseCsvGrid(trimmed, base),
      note: 'Read as a CSV colour grid — stitch set to square, adjust in Pattern settings.',
    };
  }

  throw new Error(`Cannot tell what kind of pattern ${filename} is. Supported: .bead.json, JSON colour grids, CSV.`);
}

export function patternToCsv(pattern: Pattern): string {
  const lines: string[] = [];
  for (let r = 0; r < pattern.height; r++) {
    const row: string[] = [];
    for (let c = 0; c < pattern.width; c++) {
      const v = pattern.cells[r * pattern.width + c];
      const color = pattern.palette[v];
      row.push(v === EMPTY ? '' : color?.code || color?.hex || '');
    }
    lines.push(row.join(','));
  }
  return lines.join('\n');
}

export function downloadText(filename: string, text: string, mime = 'application/json'): void {
  const blob = new Blob([text], { type: `${mime};charset=utf-8` });
  downloadBlob(filename, blob);
}

export function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function safeFilename(name: string): string {
  return name.replace(/[^\w\-. ]+/g, '_').replace(/\s+/g, '-').slice(0, 80) || 'pattern';
}
